import { displayDateUS, toIsoDate } from "../../utils/dateUtils";

export const AUTH_DISCIPLINE_OPTIONS = [
  { value: "RN/LVN", label: "RN/LVN" },
  { value: "PT/PTA", label: "PT/PTA" },
  { value: "OT/COTA", label: "OT/COTA" },
  { value: "ST/SLP", label: "ST/SLP" },
  { value: "MSW", label: "MSW" },
  { value: "CHHA", label: "CHHA" },
  { value: "CG/CNA", label: "CG/CNA" },
];

/** Older rows stored HHA/CHHA — show and save as CHHA. */
export const normalizeAuthDiscipline = (value) => {
  const key = String(value || "").trim();
  if (key === "HHA/CHHA" || key === "HHA") return "CHHA";
  return key;
};

/** Options for one line — already-used disciplines on the same auth are hidden. */
export const availableAuthDisciplineOptions = (disciplines, currentIndex) => {
  const current = normalizeAuthDiscipline(
    disciplines?.[currentIndex]?.discipline,
  );
  const used = new Set(
    (disciplines || [])
      .map((d, i) =>
        i === currentIndex
          ? ""
          : normalizeAuthDiscipline(d.discipline),
      )
      .filter(Boolean),
  );
  return AUTH_DISCIPLINE_OPTIONS.filter(
    (opt) => !used.has(opt.value) || opt.value === current,
  );
};

/**
 * Kept for future UI — Type / Insurance / Billing Code are hidden today and
 * filled with these defaults on save.
 */
export const DEFAULT_AUTH_TYPE = "Active";

export const AUTH_TYPE_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Pending", label: "Pending" },
  { value: "Expired", label: "Expired" },
  { value: "Denied", label: "Denied" },
];

/** One discipline line under an authorization. */
export const emptyAuthDiscipline = () => ({
  discipline: "",
  billingCode: "",
  authorizedUnits: "",
  usedUnits: 0,
  remainingUnits: null,
});

/**
 * Nested auth: one period/number can hold many discipline lines
 * (e.g. RN—20 + PT—20 on the same Auth #).
 */
export const emptyAuthorization = () => ({
  authType: DEFAULT_AUTH_TYPE,
  insuranceName: "",
  fromDate: "",
  toDate: "",
  authNumber: "",
  notes: "",
  disciplines: [emptyAuthDiscipline()],
});

const toDisplayDate = (value) => {
  if (!value) return "";
  if (String(value).match(/^\d{2}\/\d{2}\/\d{4}$/)) return value;
  return displayDateUS(value) || "";
};

/** Keep nested shape from API — one auth with many discipline children. */
export const buildAuthorizations = (patient) => {
  const rows = patient?.authorizations;
  if (!Array.isArray(rows) || rows.length === 0) return [];

  return rows
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((row) => {
      const disciplines = (row.disciplines || [])
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((d) => ({
          discipline: normalizeAuthDiscipline(d.discipline),
          billingCode: d.billingCode || "",
          authorizedUnits:
            d.authorizedUnits === 0 || d.authorizedUnits
              ? String(d.authorizedUnits)
              : "",
          usedUnits: d.usedUnits ?? 0,
          remainingUnits: d.remainingUnits ?? null,
        }));

      return {
        authType: row.authType || DEFAULT_AUTH_TYPE,
        insuranceName: row.insuranceName || "",
        fromDate: toDisplayDate(row.fromDate),
        toDate: toDisplayDate(row.toDate),
        authNumber: row.authNumber || "",
        notes: row.notes || "",
        disciplines: disciplines.length ? disciplines : [emptyAuthDiscipline()],
      };
    });
};

export const validateAuthorizations = (rows) => {
  for (let i = 0; i < (rows || []).length; i++) {
    const row = rows[i];
    const label = `Authorization ${i + 1}`;
    if (row.fromDate && row.toDate) {
      const fromIso = toIsoDate(row.fromDate);
      const toIso = toIsoDate(row.toDate);
      if (fromIso && toIso && toIso < fromIso) {
        return `${label}: To date must be on or after From date`;
      }
    }

    const disciplines = row.disciplines || [];
    const seen = new Set();
    for (let j = 0; j < disciplines.length; j++) {
      const d = disciplines[j];
      const dLabel = `${label} · Discipline ${j + 1}`;
      if (d.authorizedUnits !== "" && Number(d.authorizedUnits) < 0) {
        return `${dLabel}: Authorized must be 0 or greater`;
      }
      const key = normalizeAuthDiscipline(d.discipline);
      if (d.authorizedUnits !== "" && !key) {
        return `${dLabel}: Select a discipline`;
      }
      if (key) {
        if (seen.has(key)) {
          return `${label}: Duplicate discipline "${key}"`;
        }
        seen.add(key);
      }
    }
  }
  return null;
};

const isDisciplineFilled = (d) =>
  Boolean(d.discipline || d.authorizedUnits !== "");

const isAuthFilled = (row) =>
  Boolean(
    row.authNumber ||
      row.fromDate ||
      row.toDate ||
      row.notes ||
      (row.disciplines || []).some(isDisciplineFilled),
  );

/**
 * Persist nested auth + discipline lines. Type / Insurance / Billing Code are
 * not collected in the UI — defaults go out so the columns stay populated for
 * later features.
 */
export const buildAuthorizationsPayload = (rows) =>
  (rows || [])
    .filter(isAuthFilled)
    .map((row) => ({
      authType: DEFAULT_AUTH_TYPE,
      insuranceName: null,
      notes: row.notes || null,
      fromDate: row.fromDate ? toIsoDate(row.fromDate) || row.fromDate : null,
      toDate: row.toDate ? toIsoDate(row.toDate) || row.toDate : null,
      authNumber: row.authNumber || null,
      disciplines: (row.disciplines || [])
        .filter(isDisciplineFilled)
        .map((d) => ({
          discipline: normalizeAuthDiscipline(d.discipline) || null,
          billingCode: null,
          billingCodeLabel: null,
          billingCodeConfirmed: false,
          authorizedUnits:
            d.authorizedUnits === "" ? 0 : Number(d.authorizedUnits) || 0,
          appliedUnits: 0,
        })),
    }));
